/* HidroVerse SalesFlow — Offline mutation queue
 *
 * Persists POST/PUT/DELETE/PATCH requests in IndexedDB when the device is
 * offline (or a request fails with a network error) and replays them when
 * connectivity returns.
 *
 * Files / blobs are also persisted directly inside IndexedDB so techs can
 * snap photos / videos in the field with no signal and have them sync the
 * moment they're back online.
 *
 * Public API:
 *   - queueRequest({ url, method, body, headers, kind, label })
 *   - drainQueue()                — manual trigger
 *   - getQueueSize() : Promise<number>
 *   - subscribe(cb)               — fired with { size } on every change
 *   - useOfflineQueue()           — React hook returning { online, size }
 */

import { useEffect, useState } from "react";

const DB_NAME = "hv-offline-queue";
const STORE = "queue";
const DB_VERSION = 1;

let _db = null;
function openDb() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => {
      _db = req.result;
      resolve(_db);
    };
    req.onerror = () => reject(req.error);
  });
}

function tx(mode = "readonly") {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

const subscribers = new Set();
function notify() {
  getQueueSize().then((size) => subscribers.forEach((cb) => cb({ size })));
}

export function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export async function getQueueSize() {
  const store = await tx();
  return new Promise((resolve) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => resolve(0);
  });
}

export async function getQueueEntries() {
  const store = await tx();
  return new Promise((resolve) => {
    const req = store.getAll();
    req.onsuccess = () => {
      const all = (req.result || []).slice().sort((a, b) => a.created_at - b.created_at);
      resolve(all);
    };
    req.onerror = () => resolve([]);
  });
}

export async function clearQueue() {
  const store = await tx("readwrite");
  await new Promise((resolve) => {
    const req = store.clear();
    req.onsuccess = resolve;
    req.onerror = resolve;
  });
  notify();
}

export async function discardEntry(id) {
  const store = await tx("readwrite");
  await new Promise((resolve) => {
    const req = store.delete(id);
    req.onsuccess = resolve;
    req.onerror = resolve;
  });
  notify();
}


/**
 * Queue a request body for later replay.
 *
 * `body` may be:
 *   - undefined / null
 *   - a JSON-serialisable object
 *   - a FormData instance (we store each entry; Blobs persist verbatim).
 */
export async function queueRequest({ url, method, body, headers, kind, label }) {
  const store = await tx("readwrite");
  const stored = serialiseBody(body);
  const entry = {
    url,
    method: (method || "GET").toUpperCase(),
    headers: headers || null,
    body: stored.body,
    bodyKind: stored.kind,
    kind: kind || "generic",
    label: label || `${method} ${url}`,
    created_at: Date.now(),
    attempts: 0,
  };
  await new Promise((resolve, reject) => {
    const req = store.add(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  notify();
}

function serialiseBody(body) {
  if (body == null) return { kind: "none", body: null };
  if (body instanceof FormData) {
    const items = [];
    for (const [k, v] of body.entries()) {
      if (v instanceof File || v instanceof Blob) {
        items.push({ key: k, kind: "blob", value: v, filename: v.name });
      } else {
        items.push({ key: k, kind: "text", value: String(v) });
      }
    }
    return { kind: "formdata", body: items };
  }
  // Plain JS object → JSON
  return { kind: "json", body };
}

function rebuildBody(stored) {
  if (stored.bodyKind === "none") return undefined;
  if (stored.bodyKind === "formdata") {
    const fd = new FormData();
    (stored.body || []).forEach((it) => {
      if (it.kind === "blob") fd.append(it.key, it.value, it.filename);
      else fd.append(it.key, it.value);
    });
    return fd;
  }
  return JSON.stringify(stored.body);
}

export async function drainQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const store = await tx("readwrite");
  const all = await new Promise((resolve) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
  // Process oldest first so dependent records replay in order.
  all.sort((a, b) => a.created_at - b.created_at);
  for (const entry of all) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await replayOne(entry);
    if (!ok) break; // stop on first failure to preserve order
  }
}

async function replayOne(entry) {
  const headers = { ...(entry.headers || {}) };
  // Wave 25.12 — the saved Authorization header on a queued entry can
  // be hours stale and the JWT may have expired. Use the CURRENT token
  // from localStorage so retry actually authenticates as the present
  // user. (The original behaviour was to replay verbatim, which is why
  // entries kept 4xx-ing forever even after the user logged back in.)
  if (typeof localStorage !== "undefined") {
    const token = localStorage.getItem("hv_token");
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let body;
  if (entry.bodyKind === "json") {
    body = rebuildBody(entry);
    if (!headers["Content-Type"]) headers["Content-Type"] = "application/json";
  } else if (entry.bodyKind === "formdata") {
    body = rebuildBody(entry);
    // Let fetch set the multipart boundary
    delete headers["Content-Type"];
  }
  try {
    const resp = await fetch(entry.url, {
      method: entry.method,
      headers,
      body,
      credentials: "include",
    });
    if (!resp.ok && resp.status >= 500) {
      // Capture the body text (truncated) so the user can see why it failed.
      let detail = "";
      try { detail = (await resp.text()).slice(0, 200); } catch (_) { /* noop */ }
      throw new Error(`server ${resp.status}${detail ? `: ${detail}` : ""}`);
    }
    if (!resp.ok && resp.status === 401) {
      // Auth lost — stop draining; the user will reauth and the queue
      // can replay then. Don't drop the entry.
      throw new Error("auth lost (401) — login expired");
    }
    // Success (or 4xx other than 401 — bad request, dropping it is correct so we don't loop forever)
    const store = await tx("readwrite");
    await new Promise((resolve) => {
      const req = store.delete(entry.id);
      req.onsuccess = resolve;
      req.onerror = resolve;
    });
    notify();
    return true;
  } catch (err) {
    // Bump attempt counter; record the last failure reason so the UI can
    // surface it. Drop after 5 attempts (Wave 25.12 — was 25, which let
    // hopelessly-stuck entries linger for days).
    const store = await tx("readwrite");
    entry.attempts = (entry.attempts || 0) + 1;
    entry.last_error = (err && err.message) ? String(err.message).slice(0, 240) : "network error";
    entry.last_error_at = Date.now();
    if (entry.attempts > 5) {
      store.delete(entry.id);
    } else {
      store.put(entry);
    }
    notify();
    return false;
  }
}

// Auto-drain on online events
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    drainQueue();
  });
  // Also try once on startup in case we already had network when we loaded
  setTimeout(drainQueue, 1500);
}

export function useOfflineQueue() {
  const [size, setSize] = useState(0);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    getQueueSize().then((s) => alive && setSize(s));
    const unsub = subscribe(({ size: s }) => alive && setSize(s));
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  return { online, queueSize: size };
}
